import { useState, useMemo, useCallback, useEffect, useRef, memo } from "react";
import useEmblaCarousel from "embla-carousel-react";
import {
  ChevronLeft,
  ChevronRight,
  Star,
  MessageCircle,
  Bookmark,
  AlertTriangle,
  Crown,
  Handshake,
  Clock,
  ExternalLink,
  Layers,
  Loader2,
  Copy,
  QrCode,
  History,
  Store,
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Badge } from "@/components/ui/badge";
import ShareMenu from "@/components/ShareMenu";
import { isNotified } from "@/lib/notified-sold";
import ReportProblemDialog from "@/components/ReportProblemDialog";
import SolicitarIntermedioDialog from "@/components/SolicitarIntermedioDialog";
import OwnerActionButtons from "@/components/OwnerActionButtons";
import ContactOptionsPopover from "@/components/ContactOptionsPopover";
import ItemHistoryDialog from "@/components/ItemHistoryDialog";

import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import rubiniCoinsIcon from "@/assets/rubini_coins.gif";
import crystalCoinIcon from "@/assets/crystal_coin.gif";
import { toast } from "@/hooks/use-toast";
import { useAnuncioPaymentPolling } from "@/hooks/useAnuncioPaymentPolling";
import { supabase } from "@/integrations/supabase/client";
import SoldRibbon from "@/components/SoldRibbon";
import logoImg from "@/assets/logo.webp";
import type { Anuncio } from "@/hooks/useAnuncios";
import { checkIsOwner } from "@/lib/ownership";

const getItemImageUrl = (name: string) => {
  const formatted = name.replace(/ /g, "_");
  return `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/proxy-item-image?name=${encodeURIComponent(formatted)}`;
};

const getFavorites = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem("rpgcoin_favorites") || "[]");
  } catch {
    return [];
  }
};

const toggleFavorite = (id: string): boolean => {
  const favs = getFavorites();
  const idx = favs.indexOf(id);
  if (idx >= 0) {
    favs.splice(idx, 1);
    localStorage.setItem("rpgcoin_favorites", JSON.stringify(favs));
    return false;
  }
  favs.push(id);
  localStorage.setItem("rpgcoin_favorites", JSON.stringify(favs));
  return true;
};

interface FeaturedCarouselProps {
  anuncios: Anuncio[];
  onCardClick: (anuncio: Anuncio) => void;
  onRequireLogin?: (action: () => void) => void;
  onApelidoClick?: (apelido: string) => void;
  rotationTime?: number;
  rotateAll?: boolean;
  fitToScreen?: boolean;
  sessionContato?: string;
  sessionToken?: string;
  sessionPerfilId?: string | null;
  ownedAnuncioIds?: Set<string>;
  onAnuncioAction?: () => void;
  intermediatingAnuncios?: Set<string>;
  interesseAnuncioIds?: Set<string>;
  /** Full lote items (across destaque + non-destaque) for QR overlay thumbnails. */
  loteFullItems?: Map<string, { item_nome: string; destaque: boolean; id: string }[]>;
}

type FeaturedCarouselItem = Anuncio & {
  _batchCount?: number;
  _batchItems?: Anuncio[];
  _loteFullItems?: { item_nome: string; destaque: boolean; id: string }[];
};

const FeaturedCarousel = ({
  anuncios,
  onCardClick,
  onRequireLogin,
  onApelidoClick,
  rotationTime = 4,
  rotateAll = false,
  fitToScreen = false,
  sessionContato,
  sessionToken,
  sessionPerfilId,
  ownedAnuncioIds,
  onAnuncioAction,
  intermediatingAnuncios,
  interesseAnuncioIds,
  loteFullItems,
}: FeaturedCarouselProps) => {
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [expandedLotes, setExpandedLotes] = useState<Set<string>>(new Set());

  const handleDismissCard = useCallback((id: string) => {
    setDismissedIds(prev => new Set(prev).add(id));
    setTimeout(() => onAnuncioAction?.(), 400);
  }, [onAnuncioAction]);

  const handleBatchToggle = useCallback((anuncio: FeaturedCarouselItem) => {
    const batchItems = anuncio._batchItems;
    if (!anuncio.lote_id || !batchItems || batchItems.length <= 1) {
      onCardClick(anuncio);
      return;
    }
    setExpandedLotes(prev => {
      const next = new Set(prev);
      if (next.has(anuncio.lote_id!)) {
        next.delete(anuncio.lote_id!);
      } else {
        next.add(anuncio.lote_id!);
      }
      return next;
    });
  }, [onCardClick]);

  // Clear dismissed IDs when anuncios change (data refreshed)
  useEffect(() => {
    setDismissedIds(new Set());
    setExpandedLotes(new Set());
  }, [anuncios]);

  const featured = useMemo(() => {
    const nowMs = Date.now();
    // An ad is featured when destaque=true OR it is still within its destaque window
    // (destaque_fim > now) — the latter happens after a destaque→geral downgrade renewal,
    // where the ad must stay in the highlight area until the original period ends.
    const isStillFeatured = (a: any) =>
      a.destaque || (a.destaque_fim && new Date(a.destaque_fim).getTime() > nowMs);
    const destaques = anuncios.filter((a) => isStillFeatured(a) && !dismissedIds.has(a.id));
    const lotes = new Map<string, Anuncio[]>();

    for (const anuncio of destaques) {
      if (!anuncio.lote_id) continue;
      // Only group pending/awaiting payment items; approved items show individually
      if (anuncio.status !== "pendente" && anuncio.status !== "aguardando_pagamento") continue;
      const current = lotes.get(anuncio.lote_id) || [];
      current.push(anuncio);
      lotes.set(anuncio.lote_id, current);
    }

    const seenLotes = new Set<string>();
    const result: FeaturedCarouselItem[] = [];

    for (const anuncio of destaques) {
      if (!anuncio.lote_id || !lotes.has(anuncio.lote_id)) {
        // No lote or not in a pending batch → show individually
        result.push(anuncio);
        continue;
      }

      // Pending/awaiting payment batch logic
      if (seenLotes.has(anuncio.lote_id)) continue;
      seenLotes.add(anuncio.lote_id);

      const batchItems = lotes.get(anuncio.lote_id)!;
      const fullLote = loteFullItems?.get(anuncio.lote_id);

      if (batchItems.length > 1) {
        if (expandedLotes.has(anuncio.lote_id)) {
          batchItems.forEach((item) => {
            result.push({
              ...item,
              _batchCount: batchItems.length,
              _batchItems: batchItems,
              _loteFullItems: fullLote,
              _isExpandedItem: true,
            } as any);
          });
        } else {
          result.push({
            ...anuncio,
            _batchCount: batchItems.length,
            _batchItems: batchItems,
            _loteFullItems: fullLote,
          } as any);
        }
        continue;
      }

      // Single destaque item but lote has additional non-destaque siblings → still attach for overlay
      if (fullLote && fullLote.length > 1) {
        result.push({ ...anuncio, _loteFullItems: fullLote } as any);
        continue;
      }

      result.push(anuncio);
    }

    return result;
  }, [anuncios, dismissedIds, expandedLotes, loteFullItems]);

  const [windowWidth, setWindowWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1024);

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const slidesPerView = windowWidth < 640 ? 2 : windowWidth < 1024 ? 3 : 4;

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    slidesToScroll: slidesPerView,
  });

  // Track which slides are near the viewport for windowed rendering
  const [visibleRange, setVisibleRange] = useState<Set<number>>(new Set());

  const updateVisibleRange = useCallback(() => {
    if (!emblaApi) return;
    const inView = emblaApi.slidesInView();
    const buffer = slidesPerView * 2; // render 2 pages ahead/behind
    const total = featured.length;
    const newSet = new Set<number>();
    for (const idx of inView) {
      for (let i = idx - buffer; i <= idx + buffer; i++) {
        // Handle loop wrapping
        newSet.add(((i % total) + total) % total);
      }
    }
    setVisibleRange(newSet);
  }, [emblaApi, slidesPerView, featured.length]);

  useEffect(() => {
    if (!emblaApi) return;
    updateVisibleRange();
    emblaApi.on("scroll", updateVisibleRange);
    emblaApi.on("reInit", updateVisibleRange);
    return () => {
      emblaApi.off("scroll", updateVisibleRange);
      emblaApi.off("reInit", updateVisibleRange);
    };
  }, [emblaApi, updateVisibleRange]);

  // ReInit Embla when featured list changes (e.g. after dismissing a card)
  useEffect(() => {
    emblaApi?.reInit();
  }, [featured, emblaApi]);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!emblaApi || featured.length <= 1) return;
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      if (document.hidden) return;
      // Don't auto-scroll if a dialog/modal is open (prevents unmounting cards with open dialogs)
      if (document.querySelector('[role="dialog"]')) return;
      // Don't auto-scroll if a payment QR is visible
      if (containerRef.current?.querySelector('[data-payment-qr-open]')) return;
      const hovered = containerRef.current?.matches(":hover") ?? false;
      if (!hovered) emblaApi.scrollNext();
    }, rotationTime * 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [emblaApi, featured.length, rotationTime]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  // Preload only nearby item images (not all 192)
  useEffect(() => {
    for (const idx of visibleRange) {
      if (idx < featured.length) {
        const img = new Image();
        img.src = getItemImageUrl(featured[idx].item_nome);
      }
    }
  }, [visibleRange, featured]);

  if (featured.length === 0) return null;

  return (
    <div className="mb-6 animate-fade-in">
      <div className="flex items-center gap-2 mb-3">
        <div className="animate-[pulse_2s_ease-in-out_infinite]">
          <Star className="w-4 h-4 text-primary fill-primary" />
        </div>
        <h2 className="font-pixel text-[9px] text-primary gold-text-glow tracking-wider uppercase">
          Itens em Destaque
        </h2>
        <Badge className="font-pixel text-[8px] px-1.5 py-0 bg-primary/20 text-primary border-primary/40 gold-text-glow">
          {featured.length}
        </Badge>
      </div>

      <div className="relative group" ref={containerRef}>
        {featured.length > 1 && (
          <>
            <button
              onClick={scrollPrev}
              className="absolute -left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-card/90 border border-border/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-card cursor-pointer"
            >
              <ChevronLeft className="w-4 h-4 text-foreground" />
            </button>
            <button
              onClick={scrollNext}
              className="absolute -right-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-card/90 border border-border/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-card cursor-pointer"
            >
              <ChevronRight className="w-4 h-4 text-foreground" />
            </button>
          </>
        )}

        <div ref={emblaRef} className="overflow-hidden rounded-sm py-5">
          <div className="flex -ml-3">
            {featured.map((anuncio, index) => {
              const isExpandedItem = !!(anuncio as any)._isExpandedItem;
              const batchCount = (anuncio as FeaturedCarouselItem)._batchCount || 0;
              const isCollapsed = batchCount > 1 && !isExpandedItem;
              const destaqueInicioRaw = (anuncio as any).destaque_inicio as string | null | undefined;
              const destaqueInicioDate = destaqueInicioRaw ? new Date(destaqueInicioRaw) : null;
              const isScheduledMirror = !!(destaqueInicioDate && destaqueInicioDate.getTime() > Date.now());
              const scheduledLabel = isScheduledMirror
                ? destaqueInicioDate!.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
                : null;

              return (
              <div key={anuncio.id} className="pl-3 min-w-0 flex-[0_0_50%] sm:flex-[0_0_33.33%] lg:flex-[0_0_25%]">
                {visibleRange.has(index) || visibleRange.size === 0 ? (
                  <div className="relative">
                    {/* Stack shadow layers only for collapsed batches */}
                    {isCollapsed && batchCount >= 3 && (
                      <div className="absolute inset-0 tibia-border bg-card rounded-lg" style={{ transform: "translateY(6px) translateX(4px)", opacity: 0.3 }} />
                    )}
                    {isCollapsed && batchCount >= 2 && (
                      <div className="absolute inset-0 tibia-border bg-card rounded-lg" style={{ transform: "translateY(3px) translateX(2px)", opacity: 0.5 }} />
                    )}
                    {/* Expanded item border indicator */}
                    {isExpandedItem && (
                      <div className="absolute -inset-0.5 rounded-lg border border-primary/30 pointer-events-none" style={{ zIndex: 0 }} />
                    )}
                    <div
                      className={`relative z-10 ${isScheduledMirror ? "pointer-events-none select-none" : ""}`}
                      aria-hidden={isScheduledMirror || undefined}
                    >
                      <div className={isScheduledMirror ? "opacity-60" : ""}>
                        <FeaturedCard
                          anuncio={anuncio}
                          onCardClick={onCardClick}
                          onRequireLogin={onRequireLogin}
                          onApelidoClick={onApelidoClick}
                          fitToScreen={fitToScreen}
                          totalItems={featured.length}
                          sessionContato={sessionContato}
                          sessionToken={sessionToken}
                          sessionPerfilId={sessionPerfilId}
                          ownedAnuncioIds={ownedAnuncioIds}
                          onAnuncioAction={() => handleDismissCard(anuncio.id)}
                          onAnuncioRefresh={onAnuncioAction}
                          intermediatingAnuncios={intermediatingAnuncios}
                          isInteresseMatch={!!interesseAnuncioIds?.has(anuncio.id)}
                          isFirst={index === 0}
                        />
                      </div>
                      {isScheduledMirror && (
                        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 px-3 text-center bg-background/70 rounded-lg backdrop-blur-[1px]">
                          <span className="font-pixel text-[8px] uppercase tracking-wide text-primary-foreground bg-primary/90 px-2 py-1 rounded-sm shadow">
                            Pré-visualização
                          </span>
                          <span className="text-[11px] font-medium text-foreground leading-tight">
                            Aparecerá em destaque a partir de
                          </span>
                          <span className="text-xs font-bold text-primary leading-tight">
                            {scheduledLabel}
                          </span>
                          <span className="text-[9px] text-muted-foreground italic">
                            Visível apenas para você
                          </span>
                        </div>
                      )}
                    </div>
                    {/* Expand button for collapsed batches */}
                    {isCollapsed && !isScheduledMirror && (
                      <button
                        type="button"
                        className="absolute bottom-0 right-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary text-primary-foreground shadow-lg border-2 border-background cursor-pointer hover:brightness-110 transition-all"
                        style={{ zIndex: 9999, position: "absolute", pointerEvents: "auto" }}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleBatchToggle(anuncio as FeaturedCarouselItem);
                        }}
                      >
                        <Layers className="w-3 h-3" />
                        <span className="font-pixel text-[7px]">{batchCount} itens</span>
                      </button>
                    )}
                    {/* Collapse button for expanded batch items */}
                    {isExpandedItem && !isScheduledMirror && (
                      <button
                        type="button"
                        className="absolute bottom-0 right-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-muted-foreground shadow-lg border-2 border-background cursor-pointer hover:brightness-110 transition-all"
                        style={{ zIndex: 9999, position: "absolute", pointerEvents: "auto" }}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          if (anuncio.lote_id) {
                            setExpandedLotes(prev => {
                              const next = new Set(prev);
                              next.delete(anuncio.lote_id!);
                              return next;
                            });
                          }
                        }}
                      >
                        <ChevronLeft className="w-3 h-3" />
                        <span className="font-pixel text-[7px]">Recolher</span>
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="featured-card-glow rounded-lg" style={{ minHeight: "280px" }} />
                )}
              </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Individual card with action buttons ─── */

/* ─── Individual card with action buttons ─── */

interface FeaturedCardProps {
  anuncio: Anuncio;
  onCardClick: (anuncio: Anuncio) => void;
  onRequireLogin?: (action: () => void) => void;
  onApelidoClick?: (apelido: string) => void;
  fitToScreen: boolean;
  totalItems: number;
  sessionContato?: string;
  sessionToken?: string;
  sessionPerfilId?: string | null;
  ownedAnuncioIds?: Set<string>;
  onAnuncioAction?: () => void;
  onAnuncioRefresh?: () => void;
  intermediatingAnuncios?: Set<string>;
  isInteresseMatch?: boolean;
  isFirst?: boolean;
}

const FeaturedCard = memo(({
  anuncio,
  onCardClick,
  onRequireLogin,
  onApelidoClick,
  fitToScreen,
  totalItems,
  sessionContato,
  sessionToken,
  sessionPerfilId,
  ownedAnuncioIds,
  onAnuncioAction,
  onAnuncioRefresh,
  intermediatingAnuncios,
  isInteresseMatch,
  isFirst,
}: FeaturedCardProps) => {
  const [isFav, setIsFav] = useState(() => getFavorites().includes(anuncio.id));
  const [reportOpen, setReportOpen] = useState(false);
  const [intermedioOpen, setIntermedioOpen] = useState(false);
  const [isReported, setIsReported] = useState(() => isNotified(anuncio.id));
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [paymentQr, setPaymentQr] = useState<{ base64: string; text: string | null; valorCobrado?: number } | null>(null);
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    setDismissed(false);
  }, [anuncio.status]);

  const isRenewalPending = !!(anuncio as any).renovacao_pendente && anuncio.status !== "aguardando_pagamento";
  const isRenewalPaidByCoins = isRenewalPending && (anuncio as any).metodo_pagamento_taxa === "coins" && (anuncio as any).taxa_paga === true;
  const isAwaitingPayment = anuncio.status === "aguardando_pagamento" || isRenewalPending;
  const { confirmed: paymentConfirmed } = useAnuncioPaymentPolling({
    active: !!paymentQr && isAwaitingPayment,
    anuncioId: anuncio.id,
    sessionContato,
    sessionToken,
    onConfirmed: () => {
      // Keep QR overlay mounted briefly so the success tick stays visible,
      // then clear and refresh the listing.
      setTimeout(() => {
        setPaymentQr(null);
        (onAnuncioRefresh || onAnuncioAction)?.();
      }, 1500);
    },
  });

  const handleAnuncioAction = () => {
    setDismissed(true);
    setTimeout(() => onAnuncioAction?.(), 400);
  };

  const isOwner = checkIsOwner(
    sessionContato ? { contato: sessionContato, perfil_id: sessionPerfilId || null } : null,
    anuncio as any,
    ownedAnuncioIds,
  );
  const showBrlPrice = anuncio.moeda === "brl" && anuncio.preco > 0;
  const showTcPrice = (anuncio.preco_coins != null && anuncio.preco_coins > 0) || (anuncio.moeda === "coins" && anuncio.preco > 0);
  const showKkPrice = anuncio.preco_kk != null && anuncio.preco_kk > 0;
  const allThreePrices = showBrlPrice && showTcPrice && showKkPrice;
  const totalPriceLines = Number(showBrlPrice) + Number(showTcPrice) + Number(showKkPrice);
  const priceGap = totalPriceLines >= 3 ? "gap-1" : "gap-1.5";
  const featLabelSize = allThreePrices ? "text-[8px]" : "text-[10px]";
  const featValueSize = allThreePrices ? "text-[9px]" : "text-[12px]";

  const handleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    const doFav = () => {
      const added = toggleFavorite(anuncio.id);
      setIsFav(added);
      toast({ title: added ? "Adicionado à lista!" : "Removido da lista" });
    };
    if (onRequireLogin) onRequireLogin(doFav);
    else doFav();
  };

  const handleReportClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const doOpen = () => setReportOpen(true);
    if (onRequireLogin) onRequireLogin(doOpen);
    else doOpen();
  };

  const basisClass = "w-full";

  return (
    <>
      <div
        className={`flex-shrink-0 ${basisClass} cursor-pointer transition-all duration-300 ${dismissed ? "opacity-0 scale-90" : "opacity-100 scale-100"}`}
        onClick={() => onCardClick(anuncio)}
      >
        <div className={`featured-card-glow featured-card tibia-border rounded-sm overflow-hidden relative h-full flex flex-col ${isInteresseMatch ? "interesse-match-card" : ""}`}>
          {isInteresseMatch && <div className="interesse-badge">Interesse</div>}
          <div className="featured-sparkle-overlay" />

          {/* Active intermediation overlay */}
          {intermediatingAnuncios?.has(anuncio.id) && (
            <div className="absolute inset-0 bg-purple-900/40 z-20 rounded-sm flex items-center justify-center pointer-events-none">
              <div className="bg-purple-500/90 px-2 py-1 rounded-sm flex items-center gap-1">
                <Handshake className="w-3.5 h-3.5 text-white" />
                <span className="font-pixel text-[6px] text-white tracking-wider font-bold">EM INTERMÉDIO</span>
              </div>
            </div>
          )}

          {/* Loading overlay during pause/resume action */}
          {(actionLoading === "pausar" || actionLoading === "retomar") && (
            <div className="absolute inset-0 bg-black/40 z-20 rounded-sm flex items-center justify-center pointer-events-none">
              <div className="bg-card/90 px-3 py-1.5 rounded-sm flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-amber-400" />
                <span className="font-pixel text-[8px] text-amber-300 tracking-wider font-bold">
                  {actionLoading === "pausar" ? "PAUSANDO..." : "RETOMANDO..."}
                </span>
              </div>
            </div>
          )}
          {/* Paused overlay */}
          {anuncio.status === "pausado" && !(anuncio.expira_em && new Date(anuncio.expira_em) <= new Date()) && !isRenewalPending && actionLoading !== "retomar" && (
            <div className="absolute inset-0 bg-black/50 z-20 rounded-sm flex items-center justify-center pointer-events-none">
              <div className="bg-yellow-500/90 px-3 py-1 rounded-sm">
                <span className="font-pixel text-[8px] text-black tracking-wider font-bold">ANÚNCIO PAUSADO</span>
              </div>
            </div>
          )}
          {/* Expired by date overlay */}
          {anuncio.expira_em && new Date(anuncio.expira_em) <= new Date() && anuncio.status !== "vendido" && anuncio.status !== "pendente" && anuncio.status !== "aguardando_pagamento" && (
            <div className="absolute inset-0 bg-black/60 z-20 rounded-sm flex items-center justify-center pointer-events-none">
              <div className="bg-muted/90 px-3 py-1 rounded-sm">
                <span className="font-pixel text-[7px] text-muted-foreground tracking-wider font-bold">ENCERRADO</span>
              </div>
            </div>
          )}
          {/* Pending approval overlay */}
          {anuncio.status === "pendente" && (
            <div className="absolute inset-0 bg-amber-900/40 z-20 rounded-sm flex items-center justify-center pointer-events-none">
              <div className="bg-amber-500/90 px-2 py-1 rounded-sm flex items-center gap-1">
                <Clock className="w-3.5 h-3.5 text-black" />
                <span className="font-pixel text-[6px] text-black tracking-wider font-bold">AGUARDANDO APROVAÇÃO</span>
              </div>
            </div>
          )}
          {/* Awaiting payment overlay */}
          {anuncio.status === "aguardando_pagamento" && (
            <div className="absolute inset-0 bg-amber-900/40 z-20 rounded-sm flex flex-col items-center justify-center gap-1.5 pointer-events-none">
              <div className="bg-amber-500/90 px-2 py-1 rounded-sm flex flex-col items-center gap-0.5 pointer-events-none">
                <div className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5 text-black" />
                  <span className="font-pixel text-[6px] text-black tracking-wider font-bold">AGUARDANDO PAGAMENTO</span>
                </div>
                {anuncio.created_at && (
                  <span className="font-pixel text-[6px] text-black/70 tracking-wider">
                    até {(() => { try { return format(new Date(new Date(anuncio.created_at).getTime() + 24 * 60 * 60 * 1000), "dd/MM/yy HH:mm", { locale: ptBR }); } catch { return ""; } })()}
                  </span>
                )}
              </div>
              {(anuncio as any).abacatepay_url && (!(anuncio as any)._isExpandedItem || anuncio.id === (anuncio as any)._batchItems?.[0]?.id) && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    if (loadingPayment) return;
                    setLoadingPayment(true);
                    const sess = JSON.parse(localStorage.getItem("rpgcoin_client_session") || "{}");
                    supabase.functions.invoke("create-checkout", {
                      body: { transacao_id: (anuncio as any).transacao_id || anuncio.id, metodo: "pix", contato: sess.contato, token: sess.token },
                    }).then(({ data, error }) => {
                      if (error || data?.error) {
                        toast({ title: "Erro ao gerar QR Code", variant: "destructive" });
                      } else if (data?.qr_code_base64) {
                        setPaymentQr({ base64: data.qr_code_base64, text: data.qr_code || null, valorCobrado: data.valor_cobrado });
                      } else {
                        toast({ title: "Erro ao gerar pagamento", variant: "destructive" });
                      }
                    }).catch(() => {
                      toast({ title: "Erro ao gerar pagamento", variant: "destructive" });
                    }).finally(() => setLoadingPayment(false));
                  }}
                  className="pointer-events-auto flex items-center gap-1 px-2 py-0.5 rounded-sm text-[7px] font-pixel bg-primary/90 text-primary-foreground border border-primary/60 hover:bg-primary transition-colors shadow-md"
                >
                  {loadingPayment ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <QrCode className="w-3 h-3" />
                  )}
                  {loadingPayment ? "Gerando..." : "Pagar"}
                </button>
              )}
            </div>
          )}

          {/* Renewal pending overlay (blue) - only visible to owner */}
          {isRenewalPending && isOwner && (
            <div className="absolute inset-0 bg-blue-900/40 z-20 rounded-sm flex flex-col items-center justify-center gap-1.5 pointer-events-none">
              <div className="bg-blue-500/90 px-2 py-1 rounded-sm flex flex-col items-center gap-0.5 pointer-events-none">
                <div className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5 text-white" />
                  <span className="font-pixel text-[6px] text-white tracking-wider font-bold">RENOVAÇÃO PENDENTE</span>
                </div>
                <span className="font-pixel text-[5px] text-white/80 tracking-wider">
                  {isRenewalPaidByCoins ? "Aguardando aprovação" : "Aguardando pagamento"}
                </span>
              </div>
              <div className="flex flex-col items-center gap-1">
                {!isRenewalPaidByCoins && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (loadingPayment) return;
                      setLoadingPayment(true);
                      const sess = JSON.parse(localStorage.getItem("rpgcoin_client_session") || "{}");
                      supabase.functions.invoke("client-anuncio-action", {
                        body: { contato: sess.contato, token: sess.token, anuncio_id: anuncio.id, acao: "regerar_pagamento" },
                      }).then(({ data, error }) => {
                        if (error || data?.error) {
                          toast({ title: data?.error || "Erro ao gerar QR Code", variant: "destructive" });
                        } else if (data?.pix_qr_code_base64) {
                          setPaymentQr({ base64: data.pix_qr_code_base64, text: data.pix_qr_code || null, valorCobrado: data.valor_cobrado });
                        } else {
                          toast({ title: "Erro ao gerar pagamento", variant: "destructive" });
                        }
                      }).catch(() => {
                        toast({ title: "Erro ao gerar pagamento", variant: "destructive" });
                      }).finally(() => setLoadingPayment(false));
                    }}
                    className="pointer-events-auto flex items-center gap-1 px-2 py-0.5 rounded-sm text-[7px] font-pixel bg-primary/90 text-primary-foreground border border-primary/60 hover:bg-primary transition-colors shadow-md"
                  >
                    {loadingPayment ? <Loader2 className="w-3 h-3 animate-spin" /> : <QrCode className="w-3 h-3" />}
                    {loadingPayment ? "Gerando..." : "Pagar Renovação"}
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    if (loadingPayment) return;
                    setLoadingPayment(true);
                    const sess = JSON.parse(localStorage.getItem("rpgcoin_client_session") || "{}");
                    supabase.functions.invoke("client-anuncio-action", {
                      body: { contato: sess.contato, token: sess.token, anuncio_id: anuncio.id, acao: "cancelar_renovacao" },
                    }).then(({ data, error }) => {
                      if (error || data?.error) {
                        toast({ title: data?.error || "Erro ao cancelar renovação", variant: "destructive" });
                      } else {
                        toast({ title: "Renovação cancelada" });
                        setPaymentQr(null);
                        (onAnuncioRefresh || onAnuncioAction)?.();
                      }
                    }).catch(() => {
                      toast({ title: "Erro ao cancelar renovação", variant: "destructive" });
                    }).finally(() => setLoadingPayment(false));
                  }}
                  disabled={loadingPayment}
                  className="pointer-events-auto flex items-center gap-1 px-2 py-0.5 rounded-sm text-[7px] font-pixel bg-destructive/90 text-destructive-foreground border border-destructive/60 hover:bg-destructive transition-colors shadow-md disabled:opacity-50"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {/* Inline QR Code payment overlay */}
          {paymentQr && (isAwaitingPayment || paymentConfirmed) && (
            <div
              data-payment-qr-open
              className="absolute inset-0 bg-card/95 z-30 rounded-sm flex flex-col items-center justify-start gap-1 p-1 pt-2 overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {paymentConfirmed ? (
                <div className="flex flex-col items-center gap-2 py-4 animate-fade-in">
                  <div className="w-14 h-14 rounded-full bg-green-500/20 border-2 border-green-500 flex items-center justify-center">
                    <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="font-pixel text-[10px] text-green-500 font-bold">Pagamento Confirmado!</p>
                  <p className="font-pixel text-[7px] text-muted-foreground text-center">Seu anúncio está sendo processado.</p>
                </div>
              ) : (
                <>
                  {(() => {
                    const fullLote = (anuncio as any)._loteFullItems as { item_nome: string; destaque: boolean }[] | undefined;
                    const batchItems = (anuncio as any)._batchItems as any[] | undefined;
                    const meta: { item_nome: string; destaque: boolean }[] | null =
                      fullLote && fullLote.length > 1
                        ? fullLote
                        : (batchItems && batchItems.length > 1
                            ? batchItems.map((it) => ({ item_nome: it.item_nome as string, destaque: !!it.destaque }))
                            : null);
                    if (meta) {
                      const sorted = [...meta].sort((a, b) => Number(b.destaque) - Number(a.destaque));
                      const MAX_VISIBLE = 6;
                      const visible = sorted.slice(0, MAX_VISIBLE);
                      const remaining = sorted.length - visible.length;
                      return (
                        <div className="flex flex-wrap items-center justify-center gap-2 mb-2 max-w-full px-1">
                          {visible.map((it, idx) => (
                            <Tooltip key={`${it.item_nome}-${idx}`} delayDuration={0}>
                              <TooltipTrigger asChild>
                                <span className={`relative inline-flex items-center justify-center rounded-sm ${it.destaque ? "ring-2 ring-yellow-400 shadow-[0_0_6px_rgba(250,204,21,0.7)] bg-yellow-400/10" : ""}`}>
                                  <img
                                    src={getItemImageUrl(it.item_nome)}
                                    alt={it.item_nome}
                                    className="w-6 h-6 object-contain"
                                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = "hidden"; }}
                                  />
                                  {it.destaque && (
                                    <span className="absolute -top-1 -right-1 text-[8px] leading-none">⭐</span>
                                  )}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="z-[100]">
                                {it.item_nome}{it.destaque ? " (destaque)" : ""}
                              </TooltipContent>
                            </Tooltip>
                          ))}
                          {remaining > 0 && (
                            <Tooltip delayDuration={0}>
                              <TooltipTrigger asChild>
                                <span className="min-w-[22px] h-6 px-1 flex items-center justify-center rounded-full bg-primary text-primary-foreground font-pixel text-[8px] border border-background shadow">
                                  +{remaining}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="z-[100]">+{remaining} itens</TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      );
                    }
                    return (
                      <div className="flex items-center gap-1.5 mb-2">
                        <img
                          src={getItemImageUrl(anuncio.item_nome)}
                          alt={anuncio.item_nome}
                          className="w-6 h-6 object-contain"
                          onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = "hidden"; }}
                        />
                        <span className="font-pixel text-[8px] text-foreground leading-none truncate max-w-[140px]">
                          {anuncio.item_nome}
                        </span>
                      </div>
                    );
                  })()}
                  <span className="font-pixel text-[10px] text-green-400 font-bold drop-shadow-[0_0_4px_rgba(74,222,128,0.5)] leading-none">
                    R$ {(() => {
                      if (paymentQr?.valorCobrado != null) {
                        return paymentQr.valorCobrado.toLocaleString("pt-BR", { minimumFractionDigits: 2 });
                      }
                      const batchItems = (anuncio as any)._batchItems as any[] | undefined;
                      const total = batchItems && batchItems.length > 1
                        ? batchItems.reduce((sum: number, item: any) => sum + Number(item.valor_taxa || 0), 0)
                        : Number(anuncio.valor_taxa || 0);
                      return total.toLocaleString("pt-BR", { minimumFractionDigits: 2 });
                    })()}
                  </span>
                  <span className="font-pixel text-[7px] text-muted-foreground leading-none">
                    {(anuncio as any)._batchItems && (anuncio as any)._batchItems.length > 1 ? "Taxa total do lote" : "Taxa do anúncio"}
                  </span>
                  <div className="relative w-40 h-40">
                    <img
                      src={`data:image/png;base64,${paymentQr.base64}`}
                      alt="QR Code PIX"
                      className="w-40 h-40 rounded-sm border border-border"
                    />
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-md">
                        <img src={logoImg} alt="Logo" className="w-6 h-6 rounded-full object-cover" />
                      </div>
                    </div>
                  </div>
                  {paymentQr.text && (
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(paymentQr.text!);
                        toast({ title: "Código PIX copiado!" });
                      }}
                      className="flex items-center gap-1 px-2 py-0.5 rounded-sm text-[7px] font-pixel bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                    >
                      <Copy className="w-3 h-3" />
                      Copiar código
                    </button>
                  )}
                  <div className="flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
                    <span className="text-[7px] text-muted-foreground">Aguardando confirmação...</span>
                  </div>
                  <button
                    onClick={() => setPaymentQr(null)}
                    className="text-[7px] font-pixel text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Fechar
                  </button>
                </>
              )}
            </div>
          )}

          {anuncio.status === "vendido" && <SoldRibbon />}
          {anuncio.status === "encerrado" && <SoldRibbon type="cancelado" />}

          {/* BADGE HOT COM ANIMAÇÃO PULSANTE */}
          <div className="absolute top-0 right-0 z-30 w-16 h-16 overflow-hidden pointer-events-none">
            <div
              className="bg-red-600 text-white font-pixel text-[7px] font-bold py-1 w-[100px] text-center shadow-lg animate-hot-pulse"
              style={{
                position: "absolute",
                top: "2px" /* Bem no topo */,
                right: "-38px" /* Puxado bem para o canto */,
                /* Removi o transform daqui pois ele já está na classe animate-hot-pulse do CSS */
                boxShadow: "0 0 8px rgba(0,0,0,0.9)",
              }}
            >
              HOT
            </div>
          </div>

          <div
            className={`wax-seal ${anuncio.tipo === "venda" ? "wax-seal-venda" : "wax-seal-compra"}`}
            style={{ top: "32px" }}
          >
            <span className="font-pixel text-[7px] tracking-wider">
              {anuncio.tipo === "venda" ? "VENDA" : "COMPRA"}
            </span>
          </div>

          {/* Header */}
          <div className="px-3 pt-2 pb-1 relative z-[1] flex flex-col gap-1">
            <h3
              className="font-pixel text-[10px] text-primary gold-text-glow leading-tight truncate"
              title={anuncio.item_nome}
            >
              {anuncio.item_nome}
            </h3>
            <div className="flex flex-col items-end gap-0.5 relative z-[2] mt-1 pr-0.3">
              {anuncio.servidor && (
                <span className="text-[9px] text-muted-foreground whitespace-nowrap font-bold">
                  🌐 {anuncio.servidor}
                </span>
              )}
              {anuncio.expira_em && !(anuncio.status === "pendente" || anuncio.status === "aguardando_pagamento") ? (
                <span className="text-[9px] text-muted-foreground whitespace-nowrap">
                  Até:{" "}
                  {(() => {
                    try {
                      // For a destaque→geral downgrade mirror, the ad keeps appearing in the
                      // destaque area only until destaque_fim (the original expiry). The renewed
                      // expira_em (extended period) belongs to the general list, so show the
                      // original destaque expiry here, not the extended one.
                      const df = (anuncio as any).destaque_fim as string | null | undefined;
                      const effective =
                        !anuncio.destaque && df && new Date(df).getTime() > Date.now()
                          ? df
                          : anuncio.expira_em;
                      return format(new Date(effective), "dd/MM/yy HH:mm", { locale: ptBR });
                    } catch {
                      return "";
                    }
                  })()}
                </span>
              ) : (
                <span className="text-[9px] text-muted-foreground whitespace-nowrap">&nbsp;</span>
              )}
            </div>
          </div>

          {/* Imagem do Item */}
          <div className="relative flex items-center justify-center py-4 px-3 h-[100px] z-[1]">
            {/* History button - top left */}
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <button
                  onClick={(e) => { e.stopPropagation(); setHistoryOpen(true); }}
                  className="absolute top-10 left-1 z-[5] p-1.5 rounded-md bg-transparent hover:bg-[hsl(var(--gold)/0.15)] hover:scale-110 transition-all border border-[hsl(var(--gold)/0.7)] shadow-[0_0_8px_hsl(var(--gold)/0.4)]"
                  title="Histórico de anúncios"
                >
                  <History className="w-5 h-5 text-[hsl(var(--gold-bright))] drop-shadow-[0_0_3px_hsl(var(--gold)/0.7)]" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="z-[100]">Histórico do item</TooltipContent>
            </Tooltip>
            <div className="featured-item-glow" />
            <img
              src={getItemImageUrl(anuncio.item_nome)}
              alt={anuncio.item_nome}
              width={80}
              height={80}
              className="w-20 h-20 object-contain image-rendering-pixelated relative z-[1]"
              onError={(e) => {
                (e.target as HTMLImageElement).src = "/placeholder.svg";
              }}
              loading={isFirst ? "eager" : "lazy"}
              decoding="async"
              {...(isFirst ? ({ fetchpriority: "high" } as any) : {})}
            />
            {anuncio.tier != null && anuncio.tier > 0 && (
              <div className="absolute top-2 right-3 z-10">
                <div className="w-7 h-7 rounded-full bg-[#FF8C00] flex items-center justify-center border-2 border-[#FF6B00] shadow-md">
                  <span className="font-pixel text-[11px] font-bold text-white">{anuncio.tier}</span>
                </div>
              </div>
            )}
          </div>

          {/* Área de Preços */}
          <div className="featured-price-area px-3 pt-3 pb-1 relative z-[1] h-[56px] flex items-center">
            <div className={`flex flex-col ${priceGap} min-w-0 w-full`}>
              {anuncio.preco === 0 &&
              (!anuncio.preco_coins || anuncio.preco_coins === 0) &&
              (!anuncio.preco_kk || anuncio.preco_kk === 0) ? (
                <div className="flex items-center gap-1.5 leading-none">
                  <MessageCircle className="w-4 h-4 text-blue-400 shrink-0" />
                  <span className="font-pixel text-[10px] text-blue-400 line-clamp-2">Recebendo propostas...</span>
                </div>
              ) : (
                <>
                  {showBrlPrice && (
                    <div className="flex items-center gap-1.5 leading-none">
                      <span className="font-pixel text-[11px] text-green-400 font-bold shrink-0 drop-shadow-[0_0_4px_rgba(74,222,128,0.5)]">
                        R$
                      </span>
                      <span
                        className={`font-pixel ${featValueSize} text-primary gold-text-glow font-bold truncate leading-none`}
                      >
                        {anuncio.preco.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}
                  {showTcPrice && (
                    <div className="flex items-center gap-1.5 leading-none">
                      <img src={rubiniCoinsIcon} alt="RC" className="w-3.5 h-3.5 shrink-0" />
                      <span
                        className={`font-pixel ${featValueSize} text-primary gold-text-glow font-bold truncate leading-none`}
                      >
                        {(anuncio.preco_coins || anuncio.preco).toLocaleString("pt-BR")}
                      </span>
                    </div>
                  )}
                  {showKkPrice && (
                    <div className="flex items-center gap-1.5 leading-none">
                      <img src={crystalCoinIcon} alt="KK" className="w-3.5 h-3.5 shrink-0" />
                      <span
                        className={`font-pixel ${featValueSize} text-amber-400 gold-text-glow font-bold truncate leading-none`}
                      >
                        {anuncio.preco_kk!.toLocaleString("pt-BR")}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Descrição e Apelido */}
          <div className="px-3 pb-1 relative z-[1] flex flex-col justify-center gap-1">
            <div className="flex items-start justify-between gap-1">
              <p className="text-[8px] text-muted-foreground flex-1 line-clamp-2 min-h-[24px] break-all" title={anuncio.descricao || ""}>
                {anuncio.descricao || "\u00A0"}
              </p>
            </div>
            <div className="flex items-center justify-between gap-1">
              {isOwner && (
                <div className="flex items-center gap-0.5 bg-primary/90 text-primary-foreground px-1.5 py-0.5 rounded-sm">
                  <Crown className="w-3 h-3" />
                  <span className="font-pixel text-[6px] tracking-wider">MEU</span>
                </div>
              )}
              {anuncio.apelido && (
                <div className="ml-auto mb-1.5">
                  <Badge
                    title={(anuncio as any).revendedor ? "Revendedor" : undefined}
                    className={`font-pixel text-[6px] px-1.5 py-0.5 rounded-sm cursor-pointer transition-colors inline-flex items-center gap-1 ${
                      (anuncio as any).revendedor
                        ? "bg-indigo-600 hover:bg-indigo-500 text-white border-indigo-400 shadow-[0_0_8px_hsl(231_85%_60%/0.6)]"
                        : "bg-primary hover:bg-primary/90 text-primary-foreground border-primary gold-glow"
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onApelidoClick?.(anuncio.apelido!);
                    }}
                  >
                    {(anuncio as any).revendedor && <Store className="w-2 h-2" />}
                    {anuncio.apelido}
                  </Badge>
                </div>
              )}
            </div>
          </div>

          {/* Footer buttons */}
          {isOwner ? (
            <div
              className={`relative ${anuncio.status === "pausado" || anuncio.status === "pendente" || anuncio.status === "aguardando_pagamento" ? "z-40" : "z-[1]"} bg-amber-300/20 border-t border-amber-400/30`}
            >
              <OwnerActionButtons
                anuncioId={anuncio.id}
                anuncioStatus={anuncio.status}
                expiraEm={anuncio.expira_em}
                isDestaque={anuncio.destaque}
                destaqueInicio={(anuncio as any).destaque_inicio || null}
                sessionContato={sessionContato}
                sessionToken={sessionToken}
                actionLoading={actionLoading}
                setActionLoading={setActionLoading}
                onAnuncioAction={handleAnuncioAction}
                onAnuncioRefresh={onAnuncioRefresh}
                renovacaoPendente={!!anuncio.renovacao_pendente}
                itemNome={anuncio.item_nome}
                itemImageUrl={getItemImageUrl(anuncio.item_nome)}
                isBatchStacked={!!(anuncio as any)._batchCount && (anuncio as any)._batchCount > 1}
                batchCount={(anuncio as any)._batchCount}
              />
            </div>
          ) : (
            <div className="border-t border-border/40 flex items-center justify-between w-full py-1.5 relative z-[1]">
              <div className="flex-1 flex justify-center">
              <ContactOptionsPopover
                anuncioId={anuncio.id}
                itemNome={anuncio.item_nome}
                temWhatsapp={anuncio.tem_whatsapp}
                temEmail={anuncio.tem_email}
                anuncianteId={anuncio.anunciante_id}
                anuncianteApelido={anuncio.apelido}
                anuncianteAvatarUrl={anuncio.anunciante_avatar_url}
                preferenciasContato={anuncio.preferencias_contato || ["plataforma"]}
                onRequireLogin={onRequireLogin}
                sessionContato={sessionContato}
                sessionToken={sessionToken}
              >
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <button className="p-1 hover:scale-110 transition-all">
                      <MessageCircle className="w-3.5 h-3.5 text-green-500/70 hover:text-green-400" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="z-[100]">Contatar</TooltipContent>
                </Tooltip>
              </ContactOptionsPopover>
              </div>

              <div className="flex-1 flex justify-center">
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <button
                    onClick={(e) => { e.stopPropagation(); const doOpen = () => setIntermedioOpen(true); if (onRequireLogin) onRequireLogin(doOpen); else doOpen(); }}
                    disabled={intermediatingAnuncios?.has(anuncio.id)}
                    className="p-1 hover:scale-110 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
                  >
                    <Handshake className="w-3.5 h-3.5 text-purple-400/70 hover:text-purple-400" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="z-[100]">{intermediatingAnuncios?.has(anuncio.id) ? "Em intermédio" : "Solicitar intermédio"}</TooltipContent>
              </Tooltip>
              </div>

              <div className="flex-1 flex justify-center">
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <button onClick={handleFavorite} className="p-1 hover:scale-110 transition-all">
                    <Bookmark className={`w-3.5 h-3.5 ${isFav ? "fill-primary text-primary" : "text-amber-400/70 hover:text-amber-400"}`} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="z-[100]">Adicionar à lista</TooltipContent>
              </Tooltip>
              </div>

              <div className="flex-1 flex justify-center">
              <ShareMenu
                url={`${window.location.origin}/item/${anuncio.id}`}
                text={`Confira "${anuncio.item_nome}" no RPG Coin!`}
              />
              </div>

              <div className="flex-1 flex justify-center">
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <button onClick={handleReportClick} disabled={isReported} className="p-1 hover:scale-110 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100">
                    <AlertTriangle className="w-3.5 h-3.5 text-red-400/70 hover:text-red-400" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="z-[100]">{isReported ? "Já reportado" : "Reportar problema"}</TooltipContent>
              </Tooltip>
              </div>
            </div>
          )}
        </div>
      </div>

      <ReportProblemDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        anuncioId={anuncio.id}
        itemNome={anuncio.item_nome}
        tier={anuncio.tier}
        servidor={anuncio.servidor}
        onReported={() => setIsReported(true)}
      />

      {sessionContato && sessionToken && (
        <SolicitarIntermedioDialog
          open={intermedioOpen}
          onOpenChange={setIntermedioOpen}
          anuncioId={anuncio.id}
          itemNome={anuncio.item_nome}
          preco={anuncio.preco}
          moeda={anuncio.moeda}
          precoCoins={anuncio.preco_coins}
          precoKk={anuncio.preco_kk}
          servidor={anuncio.servidor}
          descricao={anuncio.descricao}
          anuncianteApelido={anuncio.apelido}
          anuncianteAvatarUrl={anuncio.anunciante_avatar_url}
          sessionContato={sessionContato}
          sessionToken={sessionToken}
        />
      )}

      <ItemHistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        itemNome={anuncio.item_nome}
        sessionContato={sessionContato}
        sessionToken={sessionToken}
        onRequireLogin={onRequireLogin}
        intermediatingAnuncios={intermediatingAnuncios}
      />
    </>
  );
});

export default memo(FeaturedCarousel);
